import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin } from 'rxjs';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { DeveloperRiskSignal, RepositoryRiskSignal } from '../../core/models/signals.models';

@Component({
  selector: 'app-signals-page',
  standalone: true,
  imports: [],
  templateUrl: './signals-page.component.html',
  styleUrl: './signals-page.component.scss',
})
export class SignalsPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly repositorySignals = signal<RepositoryRiskSignal[]>([]);
  readonly developerSignals = signal<DeveloperRiskSignal[]>([]);

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.repositorySignals.set([]);
    this.developerSignals.set([]);

    const params = { ...this.dateRange.toQueryParams(), top: 15 };

    forkJoin({
      repos: this.analyticsService.getRepositorySignals(params),
      devs: this.analyticsService.getDeveloperSignals(params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) this.loading.set(false);
        }),
      )
      .subscribe({
        next: ({ repos, devs }) => {
          if (loadId !== this.currentLoadId) return;
          this.repositorySignals.set(repos ?? []);
          this.developerSignals.set(devs ?? []);
        },
        error: (err: unknown) => {
          if (loadId !== this.currentLoadId) return;
          console.error('Signals request failed', err);
          this.error.set(this.formatError(err, 'Risk signals could not be loaded.'));
        },
      });
  }

  riskClass(level: string): string {
    const l = level?.toLowerCase();
    if (l === 'critical') return 'risk-critical';
    if (l === 'high') return 'risk-high';
    if (l === 'medium') return 'risk-medium';
    return 'risk-low';
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') return 'Request timed out after 8s.';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return `Cannot reach backend${error.url ? ` @ ${error.url}` : ''}.`;
      return `${fallback} (${error.status}: ${error.message})`;
    }
    return fallback;
  }
}
